import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { History, TrendingUp, Calendar, Brain } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalysisHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/strategies/history?limit=20`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load analysis history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-effect border-0 shadow-lg">
        <CardContent className="flex items-center justify-center py-12">
          <div className="pulse-animation text-gray-600">Loading history...</div>
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card className="glass-effect border-0 shadow-lg">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <History className="h-16 w-16 text-gray-400 mb-4" />
          <p className="text-gray-600 text-lg">No analysis history yet</p>
          <p className="text-gray-500 text-sm mt-2">Run your first strategy analysis to see it here</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="analysis-history">
      <Card className="glass-effect border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center">
            <History className="h-6 w-6 mr-2 text-indigo-600" />
            Analysis History
          </CardTitle>
          <CardDescription>View your previous strategy analyses</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] w-full rounded-md">
            <div className="space-y-4">
              {history.map((item, index) => (
                <div
                  key={item.id || index}
                  className="bg-white bg-opacity-60 rounded-lg p-4 border border-gray-200 hover:border-indigo-300 transition-colors cursor-pointer card-hover"
                  onClick={() => setSelectedAnalysis(selectedAnalysis?.id === item.id ? null : item)}
                  data-testid={`history-item-${index}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-xl font-bold text-gray-900">{item.symbol || 'N/A'}</span>
                        <span className="text-sm bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full">
                          {(item.strategy_type || item.strategy || 'strategy')?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="flex items-center">
                          <Brain className="h-4 w-4 mr-1" />
                          {item.ai_model === 'gemini' ? 'Gemini' : 'Claude'}
                        </div>
                      </div>
                    </div>
                    {(item.recommendation || item.risk_level) && (
                    <div className="text-right">
                      {item.recommendation && (
                      <div className={`text-sm font-semibold px-3 py-1 rounded-full ${
                        item.recommendation === 'Favorable' ? 'bg-green-100 text-green-700' :
                        item.recommendation === 'Unfavorable' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {item.recommendation}
                      </div>
                      )}
                      {item.risk_level && (
                      <div className="text-xs text-gray-500 mt-1">
                        Risk: {item.risk_level}
                      </div>
                      )}
                    </div>
                    )}
                  </div>

                  {selectedAnalysis?.id === item.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                        {item.analysis}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalysisHistory;
